/**
 * PMA Xano Data Models — Single Source of Truth
 *
 * This file maps Xano API responses to typed, normalized interfaces.
 * Every component that reads Xano data MUST use these types and normalizers
 * instead of accessing raw API fields directly.
 *
 * WHY: Xano field names are inconsistent and non-obvious:
 *   - BOQ value is "boq_sum" (not "total_price" or "total_amount")
 *   - BOQ paid is "paid" at BOQ level but "paid_sum" at item level
 *   - Files size is "size" in bytes (not MB)
 *   - Diary approval is two separate booleans (contractor + supervisor)
 *
 * HOW TO USE:
 *   import { normalizeBoq, type NormalizedBoq } from '@/types/xano-models'
 *   const boqs = rawData.map(normalizeBoq)
 *
 * XANO SCHEMA REFERENCE: /Users/ronenmac/pma-xano/table/
 */

// ═══════════════════════════════════════════════════════════════════════════════
// FINANCIAL — BOQ, BOQ Items, Bills, Bill Items
// ═══════════════════════════════════════════════════════════════════════════════

/** Normalized Main BOQ (כתב כמויות חוזי) */
export interface NormalizedBoq {
  id: number
  name: string
  projectId: number
  estimateBoqId: number | null
  contractorCompanyId: number | null
  contractorId: number | null
  // Monetary values (₪)
  contractValue: number      // boq_sum — total before discount
  initialValue: number       // initial_sum — value at contract signing
  realValue: number          // real_sum — current value after discounts
  discountValue: number      // discount_sum — total discount amount
  discountPercent: number    // discount — discount percentage
  paidTotal: number          // paid — accumulated paid (₪)
  // Computed
  remaining: number          // realValue - paidTotal
  utilization: number        // (paidTotal / realValue) * 100, capped 0-999
  // Metadata
  deleted: boolean
  createdAt: string | null
  // Addons (optional, depends on endpoint)
  _company?: { id: number; name: string } | null
  _estimate?: { id: number; name: string; sum: number } | null
}

/**
 * Normalize raw Xano BOQ response → NormalizedBoq
 *
 * Works with both:
 *  - boq_main/get/all response items (flat BOQ objects)
 *  - boq_main/get/byId response.boq (nested inside response)
 */
export function normalizeBoq(raw: any): NormalizedBoq {
  if (!raw) return emptyBoq()

  const contractValue = Number(raw.boq_sum) || 0
  const realValue = Number(raw.real_sum) || 0
  const paidTotal = Number(raw.paid) || 0
  const effectiveValue = realValue || contractValue // prefer real_sum, fallback to boq_sum

  return {
    id: raw.id,
    name: raw.name || '',
    projectId: raw.project_id || raw._projectId || 0,
    estimateBoqId: raw.estimate_boq_id || null,
    contractorCompanyId: raw.contractor_company_id || null,
    contractorId: raw.contractor_id || null,
    contractValue,
    initialValue: Number(raw.initial_sum) || 0,
    realValue,
    discountValue: Number(raw.discount_sum) || 0,
    discountPercent: Number(raw.discount) || 0,
    paidTotal,
    remaining: effectiveValue - paidTotal,
    utilization: effectiveValue > 0 ? Math.min((paidTotal / effectiveValue) * 100, 999) : 0,
    deleted: !!raw.deleted,
    createdAt: raw.created_at || null,
    _company: raw._company ? { id: raw._company.id, name: raw._company.name } : null,
    _estimate: raw._boq ? { id: raw._boq.id, name: raw._boq.name, sum: Number(raw._boq.sum) || 0 } : null,
  }
}

function emptyBoq(): NormalizedBoq {
  return {
    id: 0, name: '', projectId: 0, estimateBoqId: null,
    contractorCompanyId: null, contractorId: null,
    contractValue: 0, initialValue: 0, realValue: 0,
    discountValue: 0, discountPercent: 0, paidTotal: 0,
    remaining: 0, utilization: 0, deleted: false, createdAt: null,
  }
}

/** Normalized BOQ Item (שורה בכתב כמויות) */
export interface NormalizedBoqItem {
  id: number
  boqId: number
  number: string
  description: string
  units: string
  isItem: boolean            // true = line item, false = section header
  // Quantities
  quantity: number           // amount — original BOQ quantity
  approvedQuantity: number   // approved_amount
  // Prices per unit (₪)
  unitPrice: number          // boq_cost — price before discount
  discountPrice: number      // discount_price — price after discount
  discountPercent: number    // total_discount — as decimal (0.1 = 10%)
  // Totals (₪)
  contractTotal: number      // boq_sum — qty × price at contract level
  realTotal: number          // real_sum — after discounts
  // Paid accumulation
  paidQuantity: number       // paid — accumulated paid QUANTITY
  paidTotal: number          // paid_sum — accumulated paid MONEY (₪)
  // Computed
  remainingQuantity: number  // quantity - paidQuantity
  remainingValue: number     // realTotal - paidTotal
  completion: number         // (paidQuantity / quantity) * 100
}

export function normalizeBoqItem(raw: any): NormalizedBoqItem {
  if (!raw) return emptyBoqItem()

  const quantity = Number(raw.amount) || 0
  const paidQuantity = Number(raw.paid) || 0
  const paidTotal = Number(raw.paid_sum) || 0
  const realTotal = Number(raw.real_sum) || 0

  return {
    id: raw.id,
    boqId: raw.boq_id || 0,
    number: raw.number || '',
    description: raw.description || '',
    units: raw.units || '',
    isItem: raw.is_item !== false,
    quantity,
    approvedQuantity: Number(raw.approved_amount) || 0,
    unitPrice: Number(raw.boq_cost) || 0,
    discountPrice: Number(raw.discount_price) || 0,
    discountPercent: Number(raw.total_discount) || 0,
    contractTotal: Number(raw.boq_sum) || 0,
    realTotal,
    paidQuantity,
    paidTotal,
    remainingQuantity: quantity - paidQuantity,
    remainingValue: realTotal - paidTotal,
    completion: quantity > 0 ? Math.min((paidQuantity / quantity) * 100, 100) : 0,
  }
}

function emptyBoqItem(): NormalizedBoqItem {
  return {
    id: 0, boqId: 0, number: '', description: '', units: '', isItem: true,
    quantity: 0, approvedQuantity: 0, unitPrice: 0, discountPrice: 0, discountPercent: 0,
    contractTotal: 0, realTotal: 0, paidQuantity: 0, paidTotal: 0,
    remainingQuantity: 0, remainingValue: 0, completion: 0,
  }
}

/** Bill status as returned by Xano */
export type BillStatus = 'new' | 'pending' | 'approved' | 'signed' | 'declined'

/** Normalized Bill (חשבון) */
export interface NormalizedBill {
  id: number
  boqId: number
  projectId: number
  number: string
  status: BillStatus
  // Who
  createdById: number
  supervisorId: number | null
  declinedById: number | null
  contractorCompanyId: number | null
  // When
  createdAt: string | null
  approvedAt: string | null
  signedAt: string | null
  // Booleans (source of truth for status)
  isApproved: boolean
  isSigned: boolean
  isDeclined: boolean
  // Return info
  returned: { at: string | null; by: number; reason: string } | null
  // Retention
  retentionPercent: number
  retentionSumRequested: number
  retentionSumApproved: number
  // Message
  message: { userId: number; text: string } | null
  // Flags
  deleted: boolean
}

export function normalizeBill(raw: any): NormalizedBill {
  if (!raw) return emptyBill()

  return {
    id: raw.id,
    boqId: raw.boq_id || 0,
    projectId: raw.project_id || 0,
    number: raw.number || '',
    status: raw.status || 'new',
    createdById: raw.user_id || 0,
    supervisorId: raw.supervisor || null,
    declinedById: raw.declined_by || null,
    contractorCompanyId: raw.contractor_company_id || null,
    createdAt: raw.created_at || null,
    approvedAt: raw.approved_at || null,
    signedAt: raw.signed_at || null,
    isApproved: !!raw.is_approved,
    isSigned: !!raw.is_signed,
    isDeclined: !!raw.is_declined,
    returned: raw.returned?.returned_at
      ? { at: raw.returned.returned_at, by: raw.returned.returned_by, reason: raw.returned.reason || '' }
      : null,
    retentionPercent: Number(raw.retention_percent) || 0,
    retentionSumRequested: Number(raw.retention_sum_req) || 0,
    retentionSumApproved: Number(raw.retention_sum_apr) || 0,
    message: raw.message?.text ? { userId: raw.message.user_id, text: raw.message.text } : null,
    deleted: !!raw.deleted,
  }
}

function emptyBill(): NormalizedBill {
  return {
    id: 0, boqId: 0, projectId: 0, number: '', status: 'new',
    createdById: 0, supervisorId: null, declinedById: null, contractorCompanyId: null,
    createdAt: null, approvedAt: null, signedAt: null,
    isApproved: false, isSigned: false, isDeclined: false,
    returned: null, retentionPercent: 0, retentionSumRequested: 0, retentionSumApproved: 0,
    message: null, deleted: false,
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// TASKS
// ═══════════════════════════════════════════════════════════════════════════════

/** Normalized Task (משימה) */
export interface NormalizedTask {
  id: number
  projectId: number
  title: string
  description: string
  // Status & priority
  statusId: number
  priorityId: number
  // People
  responsibleIds: number[]
  creatorId: number
  // Dates
  startAt: string | null
  finishTo: string | null
  createdAt: string | null
  lastUpdate: string | null
  statusUpdateDate: string | null
  // Hierarchy
  parentTaskId: number | null
  tasksGroupId: number | null
  // Flags
  deleted: boolean
  // Computed (requires status dictionary)
  isOverdue: boolean         // finishTo < now AND not completed/canceled
}

export function normalizeTask(raw: any): NormalizedTask {
  if (!raw) return emptyTask()

  const finishTo = raw.finish_to || null
  const now = Date.now()
  const isOverdue = finishTo ? new Date(finishTo).getTime() < now : false

  return {
    id: raw.id,
    projectId: raw.project_id || 0,
    title: raw.title || '',
    description: raw.description || '',
    statusId: raw.status_id || 0,
    priorityId: raw.priority_id || 0,
    responsibleIds: Array.isArray(raw.responsible_id) ? raw.responsible_id : [],
    creatorId: raw.creator_id || 0,
    startAt: raw.start_at || null,
    finishTo,
    createdAt: raw.created_at || null,
    lastUpdate: raw.last_update || null,
    statusUpdateDate: raw.status_update_date || null,
    parentTaskId: raw.parent_tasks_id || null,
    tasksGroupId: raw.tasks_group_id || null,
    deleted: !!raw.deleted,
    isOverdue,  // Note: needs status context to exclude completed/canceled
  }
}

function emptyTask(): NormalizedTask {
  return {
    id: 0, projectId: 0, title: '', description: '',
    statusId: 0, priorityId: 0, responsibleIds: [], creatorId: 0,
    startAt: null, finishTo: null, createdAt: null, lastUpdate: null,
    statusUpdateDate: null, parentTaskId: null, tasksGroupId: null,
    deleted: false, isOverdue: false,
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// WORK DIARY (יומן עבודה)
// ═══════════════════════════════════════════════════════════════════════════════

/** Normalized Work Diary Entry */
export interface NormalizedDiary {
  id: number
  projectId: number
  number: string
  description: string
  diaryDate: string | null
  // People
  contractorId: number | null
  supervisorId: number | null
  contractorCompanyId: number | null
  // Status
  statusId: number
  // Approval (two independent booleans)
  contractorApproved: boolean
  supervisorApproved: boolean
  isFullyApproved: boolean   // both approved
  // Weather
  weatherId: number | null
  temperature: number | null
  weatherRating: number | null
  // Location
  latitude: number | null
  longitude: number | null
  // Metadata
  createdAt: string | null
  lastUpdate: string | null
  deleted: boolean
}

export function normalizeDiary(raw: any): NormalizedDiary {
  if (!raw) return emptyDiary()

  const contractorApproved = !!raw.contractor_approval
  const supervisorApproved = !!raw.supervisor_approval

  return {
    id: raw.id,
    projectId: raw.project_id || 0,
    number: raw.number || '',
    description: raw.description || '',
    diaryDate: raw.diary_date || raw.date || null,
    contractorId: raw.contractor_id || null,
    supervisorId: raw.supervisor_id || null,
    contractorCompanyId: raw.contractor_company_id || null,
    statusId: raw.status_id || 0,
    contractorApproved,
    supervisorApproved,
    isFullyApproved: contractorApproved && supervisorApproved,
    weatherId: raw.weather_id || null,
    temperature: raw.temperature ?? null,
    weatherRating: raw.weather_rating ?? null,
    latitude: raw.latitude ?? null,
    longitude: raw.longitude ?? null,
    createdAt: raw.created_at || null,
    lastUpdate: raw.last_update || null,
    deleted: !!raw.deleted,
  }
}

function emptyDiary(): NormalizedDiary {
  return {
    id: 0, projectId: 0, number: '', description: '', diaryDate: null,
    contractorId: null, supervisorId: null, contractorCompanyId: null,
    statusId: 0, contractorApproved: false, supervisorApproved: false,
    isFullyApproved: false, weatherId: null, temperature: null,
    weatherRating: null, latitude: null, longitude: null,
    createdAt: null, lastUpdate: null, deleted: false,
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// FILES (קבצים)
// ═══════════════════════════════════════════════════════════════════════════════

/** Normalized File */
export interface NormalizedFile {
  id: number
  name: string
  type: string               // file extension/mime
  sizeBytes: number          // size — raw bytes from Xano
  sizeMB: number             // computed: sizeBytes / (1024 * 1024)
  sizeFormatted: string      // computed: "1.2 MB" or "3.4 GB"
  // Associations
  projectId: number | null
  folderId: number
  taskId: number | null
  userId: number
  // Metadata
  description: string
  fileUrl: string            // file — the actual URL/path
  createdAt: string | null
  lastUpdate: string | null
  deleted: boolean
  personal: boolean
}

export function normalizeFile(raw: any): NormalizedFile {
  if (!raw) return emptyFile()

  const sizeBytes = Number(raw.size) || 0
  const sizeMB = sizeBytes / (1024 * 1024)

  return {
    id: raw.id,
    name: raw.name || '',
    type: raw.type || '',
    sizeBytes,
    sizeMB,
    sizeFormatted: formatFileSize(sizeBytes),
    projectId: raw.project_id || null,
    folderId: raw.folders_id || 0,
    taskId: raw.task_id || null,
    userId: raw.user_id || 0,
    description: raw.description || '',
    fileUrl: raw.file || '',
    createdAt: raw.created_at || null,
    lastUpdate: raw.last_update || null,
    deleted: !!raw.deleted,
    personal: !!raw.personal,
  }
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const mb = bytes / (1024 * 1024)
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`
  if (mb >= 1) return `${mb.toFixed(1)} MB`
  return `${(bytes / 1024).toFixed(0)} KB`
}

function emptyFile(): NormalizedFile {
  return {
    id: 0, name: '', type: '', sizeBytes: 0, sizeMB: 0, sizeFormatted: '0 B',
    projectId: null, folderId: 0, taskId: null, userId: 0,
    description: '', fileUrl: '', createdAt: null, lastUpdate: null,
    deleted: false, personal: false,
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// API RESPONSE EXTRACTORS
// Xano endpoints return data in different shapes. These helpers normalize them.
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Extract array from Xano response that might be:
 * - A flat array: [item1, item2, ...]
 * - A paginated object: { items: [...], curPage, nextPage, prevPage }
 * - An object with a nested array: { boq_items: [...] }
 * - null/undefined
 */
export function extractArray<T>(
  data: any,
  normalize: (raw: any) => T,
  arrayKey?: string,
): T[] {
  if (!data) return []
  if (Array.isArray(data)) return data.map(normalize)
  if (arrayKey && Array.isArray(data[arrayKey])) return data[arrayKey].map(normalize)
  if (Array.isArray(data.items)) return data.items.map(normalize)
  return []
}
